import moment from 'moment'
import RoutinesService from 'src/services/routines'
import RoutineEventsService from 'src/services/routineEvents'
import { normalizeDaysFor, resolveAdvancedToggles } from '@/utils/routines'

// En semanal/mensual la fecha final no se captura: sale de inicio + duración. Sólo se
// recalcula al crear o cuando el usuario tocó la vigencia (`dateEdited`).
function resolveEndDate (routine) {
  if (!['weekly', 'monthly'].includes(routine.periodicity)) return
  if (routine.id && !routine.dateEdited) return
  const periodicity = routine.periodicity === 'weekly' ? 'w' : 'M'
  routine.endDate = moment(routine.startDate).add(routine.duration, periodicity).subtract(1, 'days').format('YYYY-MM-DD')
}

export default {
  getRoutines (payload) {
    this.loading = true
    
    RoutinesService.getFilteredRoutines(payload)
      .then(routines => {
        this.routines = routines
      })
      .catch(() => {
        this.routines = []
      })
      .finally(() => {
        this.loading = false
      })
  },
  getUserRoutines () {
    RoutinesService.getUserRoutines()
      .then(data => {
        this.routines = data
      })
  },
  // TKT-2245 F2 — mismo fix que createRoutine/createEvent: antes eran DOS ramas
  // huerfanas sin encadenar (el .then() de arriba y el .then() de getFilteredEvents
  // adentro), asi que un rechazo de cualquiera de las dos quedaba sin .catch() aunque
  // el llamador si capturara la promesa devuelta.
  scheduleRoutine (routineId) {
    this.loading = true
    
    // TKT-2245 — una sola cadena, y es la que se devuelve.
    //
    // Antes esto derivaba `promise.then(...)` y `promise.finally(...)` por separado y
    // devolvía la promesa ORIGINAL. Quien llamaba capturaba el rechazo de esa, pero las
    // dos cadenas derivadas quedaban sin `catch`: un 409 del guard de la Fase 1 salía
    // además como unhandled rejection. En dev eso tapa la pantalla con el overlay de
    // webpack —y el aviso con el motivo, que es justo lo que el usuario tiene que leer,
    // queda debajo.
    return RoutinesService.scheduleRoutine(routineId)
      .then(data => {
        const routines = this.routines
        const index = routines.findIndex(routine => routine.id === routineId)
        routines[index].status = data.status

        const filters = this.eventsFilters

        return RoutineEventsService.getFilteredEvents({ routineId, filters })
          .then(({ events, capabilities }) => {
            events.map(event => {
              event.data = JSON.parse(event.data)
              event.pictures = JSON.parse(event.pictures)
              return event
            })
            this.events = events
            this.routineCapabilities = capabilities
          })
      })
      .finally(() => {
        this.loading = false
      })
  },
  createRoutine (payload) {
    this.loading = true
    const routine = this.newRoutine
    resolveEndDate(routine)

    const promise = payload.routineId ? RoutinesService.updateRoutine(routine) : RoutinesService.createRoutine(routine)

    // Una sola cadena (ver scheduleRoutine): si no, el rechazo del guard se duplica en
    // cadenas derivadas sin `catch`.
    return promise
      .then(async () => {
        await RoutinesService.getFilteredRoutines(payload.filters)
          .then(routines => {
            this.routines = routines
          })
        if (payload.routineId) await this.getRoutineEvents(payload.routineId)
      })
      .finally(() => {
        this.loading = false
      })
  },
  updateRoutineName (name) {
    this.newRoutine.name = name
  },
  updateRoutineRole (role) {
    this.newRoutine.roleId = role
  },
  updateRoutineUsers (users) {
    this.newRoutine.users = users
  },
  updateRoutinePeriodicity (periodicity) {
    this.newRoutine.periodicity = periodicity
    if (periodicity !== 'daily') this.newRoutine.endDate = null
    if (periodicity === 'daily') this.newRoutine.duration = '1'
  },
  updateRoutineDuration (duration) {
    const maxDuration = this.newRoutine.periodicity === 'daily'
      ? 365
      : this.newRoutine.periodicity === 'weekly'
        ? 52
        : 12
    if (duration > 0 && duration <= maxDuration) {
      if (this.newRoutine.id && this.newRoutine.duration === '0') this.newRoutine.dateEdited = true
      this.newRoutine.duration = duration
    }
  },
  updateRoutineStartDate (date) {
    const routine = this.newRoutine
    if (routine.id && routine.status === 'created') this.updateRoutineDuration(1)
    this.newRoutine.startDate = date
  },
  updateRoutineEndDate (date) {
    const routine = this.newRoutine
    routine.endDate = date
    if (!routine.dateEdited && routine.id) routine.dateEdited = true
  },
  // TKT-2245 Fase 2 — impacto (usuarios agregados/quitados, actividades a generar/eliminar)
  // antes de confirmar un cambio global. Solo lectura: no muta la rutina.
  previewRoutineUpdate () {
    this.updatePreviewLoading = true
    this.updatePreview = null
    const routine = this.newRoutine
    // TKT-2245 F3 — misma fecha final que se va a guardar: en semanal/mensual sale de la
    // duración, y sin esto el preview de una reducción mandaba la vigencia vieja (0 a eliminar).
    resolveEndDate(routine)
    return RoutinesService.previewUpdate(routine)
      .then(preview => {
        this.updatePreview = preview
      })
      .finally(() => {
        this.updatePreviewLoading = false
      })
  },
  deleteRoutine (id) {
    this.loading = true
    RoutinesService.deleteRoutine(id)
    .then(() => {
      this.routines = this.routines.filter(routine => routine.id !== id)
    })
    .finally(() => {
      this.loading = false
    })
  },
  finishRoutine (id) {
    this.loading = true
    // TKT-2245 F1: se devuelve la promesa para que la vista pueda mostrar el rechazo del
    // guard de finalizacion (409: la rutina tiene una programacion en curso). Antes el
    // error se perdia en silencio: el dialogo se cerraba y la rutina seguia igual.
    return RoutinesService.finishRoutine(id)
      .then(() => {
        this.routines = this.routines.map(routine => {
          if (routine.id === id) routine.status = 'finished'
          return routine
        })
      })
      .finally(() => {
        this.loading = false
      })
  },
  clearRoutine () {
    this.newRoutine = {
      id: null,
      name: null,
      roleId: null,
      users: [],
      periodicity: 'daily',
      duration: '1',
      startDate: null,
      endDate: null
    }
    this.updatePreview = null
  },
  setRoutine (routineId) {
    const routine = this.routines.find(routine => routine.id === routineId)
    this.newRoutine = {
      id: routine.id,
      name: routine.name,
      roleId: Number(routine.role_id),
      users: routine.users,
      periodicity: routine.periodicity,
      duration: '0',
      startDate: routine.start_date,
      // TKT-2245 F3 — la vigencia actual se hidrata para que el diálogo la muestre al
      // editar (antes salía "Fecha Fin" vacía / "Invalid date"). `duration: '0'` sigue
      // siendo la marca de "vigencia sin tocar": sólo `dateEdited` hace que viaje. En un
      // borrador no: el fin viejo acotaría el selector de Inicio (`:end`).
      endDate: routine.status === 'created' ? null : routine.end_date,
      dateEdited: false,
      status: routine.status
    }
  },

  // Filters
  updateEventsFilters ({ routineId, filters }) {
    this.eventsFilters = filters
    this.getRoutineEvents(routineId)
  },
  clearEventsFilters () {
    this.eventsFilters = {
      type: ['event', 'form', 'task'],
      programming: ['daysHours', 'goals'],
      order: 'byCreation'
    }
  },
  getRoutineEvents (routineId) {
    this.loading = true
    this.events = []
    const filters = this.eventsFilters
    const promise = RoutineEventsService.getFilteredEvents({ routineId, filters })
      .then(({ events, capabilities }) => {
        events.map(event => {
          event.data = JSON.parse(event.data)
          event.pictures = JSON.parse(event.pictures)
          return event
        })
        this.events = events
        this.routineCapabilities = capabilities
      })
      .finally(() => {
        this.loading = false
      })

    return promise
  },
  deleteRoutineEvents (eventId) {
    this.loading = true
    const promise = RoutineEventsService.deleteEvent(eventId)
      .then(() => {
        this.events =  this.events.filter(event => event.id !== eventId)
      })
      .finally(() => {
        this.loading = false
      })

    return promise
  },
  updateEventType (type) {
    this.newEvent.type = type
    if (type === 'event') this.newEvent.programmingType = 'daysHours'
  },
  updateEventName (name) {
    this.newEvent.name = name
  },
  updateEventDescription (description) {
    this.newEvent.description = description
  },
  updateEventFrequency (frequency) {
    if (frequency) this.newEvent.frequency = frequency
    this.resetFrequencyContent()
  },
  updateEventGoal (goal) {
    const event = this.newEvent
    let maxGoal = 3
    if (event.frequency === 'weekly') maxGoal = 52
    else if (event.frequency === 'monthly') maxGoal = 365

    if (goal > 0 && maxGoal >= goal) event.goal = goal
  },
  updateEventStartHour (date) {
    this.newEvent.startHour = date
  },
  updateEventEndHour (date) {
    this.newEvent.endHour = date
  },
  updateEventDays (day) {
    const currentState = this.newEvent.days
    if (currentState.includes(day)) this.newEvent.days = currentState.filter(d => d !== day)
    else this.newEvent.days.push(day)
  },
  updateEventDay (day) {
    if (day) this.newEvent.day = day
  },
  updateEventOrdinal (ordinal) {
    if (ordinal) this.newEvent.ordinal = ordinal
  },
  updateEventForm (form) {
    this.newEvent.formId = form
  },
  updateEventProgrammingType (type) {
    if (type) {
      this.newEvent.programmingType = type
      this.newEvent.locations = []
    }
  },
  updateEventAllDay (payload) {
    this.newEvent.allDay = payload
  },
  updateEventMonthlyType (type) {
    if (type) {
      this.newEvent.monthlyType = type
      this.resetFrequencyContent()
    }
  },
  updateEventLocations (locations) {
    this.newEvent.locations = locations
  },
  resetFrequencyContent () {
    const event = this.newEvent
    if (event.ordinal !== 'first') event.ordinal = 'first'
    if (event.day.value !== 'monday') event.day = { label: 'Lu', complete: 'lunes', value: 'monday' }
    // TKT-2245 — esto vaciaba `day` (el día suelto de "El primer lunes", que es un objeto)
    // cuando lo que quería limpiar era `days`. Dejaba los selectores de "El primer lunes"
    // sin valor —y `disabled` exige `day.value`, así que el botón no se volvía a habilitar
    // y el usuario quedaba atascado— y además los nombres de día sobrevivían a mensual
    // "En los días...", donde `days` son NÚMEROS de día del mes.
    if (event.days.length > 0) event.days = []
  },
  updateEventRequirePhoto (payload) {
    this.newEvent.requiredPhoto = payload
  },
  updateEventCategory (payload) {
    this.newEvent.category = payload
  },
  updateEventCategory2 (payload) {
    this.newEvent.category2 = payload
  },
  updateEventPriority (payload) {
    this.newEvent.priority = payload
  },
  updateEventTaskByLocations (payload) {
    this.newEvent.taskByLocations = payload
    this.newEvent.locations = []
  },
  updateEventComment (payload) {
    if (payload === '') this.newEvent.comment = null
    else this.newEvent.comment = payload
  },
  updateEventPictures (payload) {
    this.newEvent.pictures = payload
  },
  updateEventLink (payload) {
    if (payload === '') this.newEvent.link = null
    else this.newEvent.link = payload
  },
  updateEventFile (payload) {
    this.newEvent.file = payload
  },
  updateEventData (payload) {
    const currentData = this.newEvent.data || {}
    const mergedData = { ...currentData, ...payload }
    this.newEvent.data = mergedData
  },
  createEvent (info) {
    this.eventLoading = true
    const payload = {
      routineId: info.routineId,
      eventId: info.eventId,
      data: this.newEvent
    }
    let promise
    if (info.eventId) {
      promise = RoutineEventsService.updateEvent(payload)
        .then(event => {
          event.data = JSON.parse(event.data)
          event.pictures = JSON.parse(event.pictures)
          const currentState = this.events
          const index = currentState.findIndex(event => event.id === info.eventId)
          currentState.splice(index, 1, event)
          this.events = currentState
        })
    } else {
      promise = RoutineEventsService.createEvent(payload)
        .then(event => {
          event.data = JSON.parse(event.data)
          event.pictures = JSON.parse(event.pictures)
          this.events.push(event)
        })
    }

    // Una sola cadena (ver scheduleRoutine). Este es el camino por el que se vio el
    // problema: al guardar una actividad con una solicitud global viva, el 409 del guard
    // salia como unhandled rejection en vez de como aviso.
    return promise.finally(() => {
      this.eventLoading = false
    })
  },
  setEvent (payload) {
    // TKT-2245 F5 — `days` entra en forma canónica. Una actividad guardada con el día
    // repetido (o con otra capitalización) no pinta el chip correspondiente, así que el
    // usuario no ve el problema y al guardar lo reenvía tal cual, duplicando otra vez la
    // semana al generar. Se limpia al hidratar, que es por donde entra el dato sucio.
    // Ojo: en mensual "En los días..." `days` son números de día del mes, no nombres de
    // día, y normalizarlos ahí borraba la selección del usuario al abrir el editor.
    if (!payload) {
      this.newEvent = payload
      return
    }

    // TKT-2245 F6/F7 — los toggles avanzados son estado de vista y nunca se persistieron;
    // se derivan de lo que sí quedó guardado, o reabrir una actividad ya configurada los
    // muestra apagados y el usuario no ve su propia configuración por día.
    this.newEvent = {
      ...payload,
      ...(Array.isArray(payload.days) ? { days: normalizeDaysFor(payload.days, payload.frequency, payload.monthlyType) } : {}),
      ...resolveAdvancedToggles(payload)
    }
  },
  clearEvent () {
    this.newEvent = {
      type: null,
      name: null,
      formId: null,
      programmingType: 'goals',
      monthlyType: 'the',
      description: null,
      frequency: 'daily',
      goal: '1',
      days: [],
      day: { label: 'Lu', complete: 'lunes', value: 'monday' },
      ordinal: 'first',
      startHour: null,
      endHour: null,
      allDay: false,
      enforceDeadline: false,
      locations: [],
      requiredPhoto: false,
      category: null,
      category2: null,
      priority: null,
      taskByLocations: false,
      comment: null,
      pictures: [],
      file: null,
      link: null,
      data: {},
      showDayConfig: false,
      showMultipleVisits: false
    }
  },
  /**
   * TKT-2245 F6 — apagar la opción tiene que BORRAR lo que configuró.
   *
   * `LocationsByUserSelector` tenía watchers para esto, pero observaban propiedades que
   * no existen en el componente (`showDayConfig` no es prop ni data suya, y su
   * `showMultipleVisits` local no es el que toca el toggle, que escribe al store). O sea
   * que nunca corrían: el usuario apagaba la opción, guardaba, y la configuración por día
   * seguía intacta — la actividad se marcaba para reprogramar y la agenda se regeneraba
   * con el patrón que acababa de desactivar. Verificado en la app.
   *
   * La limpieza vive aquí, en el único punto por el que pasa el toggle.
   */
  updateEventShowDayConfig (payload) {
    this.newEvent.showDayConfig = payload

    if (payload) {
      this.newEvent.taskByLocations = true
      return
    }

    this.newEvent.showMultipleVisits = false

    // Se borra la configuración por día, NO las ubicaciones. Apagar la opción significa
    // "todos los días en las ubicaciones de siempre", no "esta actividad se queda sin
    // ubicación": vaciar `locations` hace que el backend marque todas las relaciones como
    // borradas y la generación cae al rol, produciendo actividades sin ubicación.
    // Verificado en la app — por eso el watcher original, que hacía justo eso, era peor
    // que no correr.
    this.updateEventData({ userDayAssignments: {}, userDayTimeSlots: {}, hasMultipleVisits: false })
  },
  updateEventShowMultipleVisits (payload) {
    this.newEvent.showMultipleVisits = payload

    // Apagar sólo las visitas múltiples conserva la configuración por día: la opción
    // anidada se va, la de arriba se queda.
    if (!payload) {
      this.updateEventData({ userDayTimeSlots: {}, hasMultipleVisits: false })
    }
  },
  updateEventEnforceDeadline (payload) {
    this.newEvent.enforceDeadline = payload
  }
}
